/**
 * Client-side rate limiter to prevent brute force form submissions.
 * Uses localStorage to persist attempt counts across page reloads.
 *
 * Configuration (editable):
 * - MAX_ATTEMPTS: Maximum submissions allowed within the time window
 * - WINDOW_MS: Time window in milliseconds (default: 15 minutes)
 * - LOCKOUT_MS: How long to lock out after exceeding max attempts (default: 30 minutes)
 */

const RATE_LIMIT_CONFIG = {
    MAX_ATTEMPTS: 5,
    WINDOW_MS: 15 * 60 * 1000,      // 15 minutes
    LOCKOUT_MS: 30 * 60 * 1000,     // 30 minutes lockout
};

function getStorageKey(formId) {
    return `rate_limit_${formId}`;
}

function getAttempts(formId) {
    try {
        const raw = localStorage.getItem(getStorageKey(formId));
        if (!raw) return { attempts: [], lockedUntil: null };
        return JSON.parse(raw);
    } catch {
        return { attempts: [], lockedUntil: null };
    }
}

function saveAttempts(formId, data) {
    try {
        localStorage.setItem(getStorageKey(formId), JSON.stringify(data));
    } catch {
        // localStorage unavailable — fail open
    }
}

/**
 * Check if the user is currently rate-limited.
 * @param {string} formId - Unique identifier for the form (e.g., 'contact', 'register')
 * @returns {{ allowed: boolean, remainingAttempts: number, retryAfterMs: number | null }}
 */
export function checkRateLimit(formId) {
    const now = Date.now();
    const data = getAttempts(formId);

    // Check lockout
    if (data.lockedUntil && now < data.lockedUntil) {
        const retryAfterMs = data.lockedUntil - now;
        return { allowed: false, remainingAttempts: 0, retryAfterMs };
    }

    // Clear lockout if expired
    if (data.lockedUntil && now >= data.lockedUntil) {
        data.lockedUntil = null;
        data.attempts = [];
        saveAttempts(formId, data);
    }

    // Filter attempts within the current window
    const recentAttempts = (data.attempts || []).filter(
        ts => now - ts < RATE_LIMIT_CONFIG.WINDOW_MS
    );

    const remainingAttempts = RATE_LIMIT_CONFIG.MAX_ATTEMPTS - recentAttempts.length;

    return { allowed: remainingAttempts > 0, remainingAttempts, retryAfterMs: null };
}

/**
 * Record a form submission attempt.
 * @param {string} formId - Unique identifier for the form
 * @returns {{ allowed: boolean, remainingAttempts: number, retryAfterMs: number | null }}
 */
export function recordAttempt(formId) {
    const now = Date.now();
    const data = getAttempts(formId);

    // Filter to recent attempts only
    data.attempts = (data.attempts || []).filter(
        ts => now - ts < RATE_LIMIT_CONFIG.WINDOW_MS
    );

    // Add current attempt
    data.attempts.push(now);

    // Check if we've exceeded the limit
    if (data.attempts.length > RATE_LIMIT_CONFIG.MAX_ATTEMPTS) {
        data.lockedUntil = now + RATE_LIMIT_CONFIG.LOCKOUT_MS;
        saveAttempts(formId, data);
        const retryAfterMs = RATE_LIMIT_CONFIG.LOCKOUT_MS;
        return { allowed: false, remainingAttempts: 0, retryAfterMs };
    }

    saveAttempts(formId, data);
    const remainingAttempts = RATE_LIMIT_CONFIG.MAX_ATTEMPTS - data.attempts.length;
    return { allowed: true, remainingAttempts, retryAfterMs: null };
}

/**
 * Format milliseconds into a human-readable string.
 * @param {number} ms
 * @returns {string}
 */
export function formatRetryTime(ms) {
    if (!ms) return '';
    const minutes = Math.ceil(ms / 60000);
    if (minutes < 2) return 'about 1 minute';
    return `${minutes} minutes`;
}
